import {
  FindingsList,
  PainFindings,
  RashFindings,
  SwellingFindings
} from 'app/components/medical/localized-findings/localized-findings';
import {
  PainForm,
  RashForm,
  SwellingForm
} from 'app/components/moderations/diagnoses/versions/localized-finding-form';
import { usePopup } from 'app/hooks/utils/popup';
import {
  LocalizedFinding,
  Pain,
  Rash,
  Swelling
} from 'lib/models/diagnoses/localized-finding';
import { LocalizedFindingItem } from 'lib/models/searches/localized-findings';
import { ClassList } from 'lib/utils/react/classlist';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IoMdStarOutline } from 'react-icons/io';
import { LuFileEdit, LuUndo, LuX } from 'react-icons/lu';
import { SearchQueryProps } from '../props';

/**
 * Props for a selected localized finding item component.
 */
type SelectedLocalizedFindingListProps = {
  items: LocalizedFindingItem[];
  form: UseFormReturn<SearchQueryProps, any, undefined>;
};

/**
 * Props for a selected localized finding item component.
 */
type SelectedLocalizedFindingListItemProps = {
  findingItem: LocalizedFindingItem;
  index: number;
  form: UseFormReturn<SearchQueryProps, any, undefined>;
};

/**
 * List of selected localized findings (which may be highlighted).
 */
export const SelectedLocalizedFindingList: React.FC<
  SelectedLocalizedFindingListProps
> = ({ items, form }) => {
  return (
    <div className="localized-finding-list">
      {items.map((findingItem, index) => (
        <SelectedLocalizedFindingListItem
          key={findingItem.finding.key()}
          index={index}
          findingItem={findingItem}
          form={form}
        />
      ))}
    </div>
  );
};

/**
 * A selected localized finding item in a list.
 */
export const SelectedLocalizedFindingListItem: React.FC<
  SelectedLocalizedFindingListItemProps
> = ({ findingItem, index, form }) => {
  // Enable popups support
  const { enqueuePopup } = usePopup();

  // Variables
  const finding = findingItem.finding;
  const isHighlighted = findingItem.isHighlighted;
  const type = finding.type;

  const isSwelling = finding.isSwelling();
  const isPain = finding.isPain();
  const isRash = finding.isRash();

  const classes = ClassList.of('localized-finding-item').add(
    'highlighted',
    isHighlighted
  );

  return (
    <div className={classes.names}>
      <div className="localized-finding-item-type">
        <span className="localized-finding-item-title selection-name">
          {type.label}
        </span>
        <div className="localized-finding-item-buttons">
          <a
            className="button button-xs button-secondary opacity-50"
            onClick={() => {
              // Determine the localized finding form to use
              const LocalizedForm = (() => {
                if (isPain) return PainForm;
                if (isRash) return RashForm;
                if (isSwelling) return SwellingForm;
                throw new Error('Unsupported localized finding type');
              })();
              // Open popup to edit localized finding
              enqueuePopup({
                component: ({ setClosing }) => (
                  <>
                    <a className="button button-secondary" onClick={setClosing}>
                      <LuUndo />
                      <span>Cancel edit</span>
                    </a>
                    <LocalizedForm
                      initial={finding as any}
                      isShowSensitivity={false}
                      onCreated={(editedItem: LocalizedFinding) => {
                        console.log(
                          'onCreated called with editedItem:',
                          editedItem.radiates.map((r) => r.label)
                        );
                        setClosing();
                        const newValues = [...form.getValues().localizedFindings];
                        const i = index;
                        console.log('Item index to update:', i);
                        newValues[i] = new LocalizedFindingItem({
                          finding: editedItem,
                          isHighlighted: newValues[i].isHighlighted
                        });
                        form.setValue('localizedFindings', newValues);
                      }}
                    />
                  </>
                ),
                allowOutsideClick: false
              });
            }}
            title="Edit localized finding"
          >
            <span>Edit</span>
            <LuFileEdit />
          </a>
          <button
            title={
              (isHighlighted ? 'Highlighted. ' : 'Not highlighted. ') +
              `When a ${type.labelLower} is highlighted, the search results hides diagnoses without this ${type.labelLower}.`
            }
            className={
              'button button-xs button-secondary' +
              (isHighlighted ? ' button-accent opacity-50' : ' opacity-50')
            }
            onClick={() => {
              const newValues = [...form.getValues().localizedFindings];
              const i = newValues.findIndex(
                (item) => item.finding.key() === finding.key()
              );
              newValues[i] = new LocalizedFindingItem({
                finding: finding,
                isHighlighted: !isHighlighted
              });
              form.setValue('localizedFindings', newValues);
            }}
          >
            <IoMdStarOutline />
          </button>
          <a
            className="button button-xs button-secondary opacity-50"
            onClick={() => {
              const calculatedKey = finding.key();
              const newValues = [...form.getValues().localizedFindings].filter(
                (item) => item.finding.key() !== calculatedKey
              );
              form.setValue('localizedFindings', newValues);
            }}
            title={`Remove ${finding.type.labelLower} from search`}
          >
            <LuX />
          </a>
        </div>
      </div>
      <div className="localized-finding-item-content">
        <FindingsList title="Radiates" labels={finding.radiates} />
        {isPain ? <PainFindings finding={finding as Pain} /> : null}
        {isSwelling ? <SwellingFindings finding={finding as Swelling} /> : null}
        {isRash ? <RashFindings finding={finding as Rash} /> : null}
      </div>
    </div>
  );
};
